/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { Component, EventEmitter, OnInit, Output, ViewChild, inject } from '@angular/core';
import { LoggerService } from '../../shared/services/logger.service';
import { CriteriaFormComponent } from './criteria-form.component';
import { SearchState } from '../state/search.state';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { SearchFormModel, SearchListEntry } from '../state/search.model';
import { DashGisUtil } from '../../shared/dash-gis-util';
import { SetSearchCriteria } from '../state/search.actions';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

@Component({
    template: '<div></div>',
    standalone: false
})
export abstract class SearchCriteriaContainerComponent implements OnInit {
    @Output() public back: EventEmitter<any> = new EventEmitter();
    @ViewChild(CriteriaFormComponent) public criteriaForm: CriteriaFormComponent;
    public search$: Observable<SearchListEntry>;
    public criteria$: Observable<SearchFormModel>;
    public criteriaFormGroup: UntypedFormGroup;
    protected _criteria$: Observable<(string) => SearchFormModel> = inject(Store).select(SearchState.currentCriteria);
    protected _search$: Observable<(string) => SearchListEntry> = inject(Store).select(SearchState.currentSearch);

    protected constructor(protected logger: LoggerService, protected fb: UntypedFormBuilder, protected store: Store) {}

    public ngOnInit() {}

    abstract search();

    protected setupFormGroup(criteria: SearchFormModel) {
        const controls = [];
        // this.fields = [];
        criteria.fields.forEach((fieldModel) => {
            const groupConfig = {};
            groupConfig[fieldModel.fieldValue.name] = fieldModel.fieldValue.value;
            groupConfig[fieldModel.operator.name] = fieldModel.operator.value || fieldModel.operator.operators[0].type;

            const group = this.fb.group(groupConfig);
            controls.push(group);
        });
        this.criteriaFormGroup = this.fb.group({
            fields: this.fb.array(controls),
        });

        this.criteriaFormGroup.valueChanges.subscribe((valueChanges) => {
            const criteriaValues = [];
            valueChanges.fields.forEach((criteriaChange) => {
                Object.keys(criteriaChange).forEach((key) => {
                    if (!key.endsWith('_op')) {
                        const searchField = criteria.fields.find((field) => field.fieldValue.name === key);
                        const f = DashGisUtil.clone(searchField);
                        if (f) {
                            f.fieldValue.value = criteriaChange[key];
                            f.operator.value = criteriaChange[`${key}_op`];
                            criteriaValues.push(f);
                        }
                    }
                });
            });
            this.store.dispatch(
                new SetSearchCriteria({
                    instanceId: this.instanceId,
                    criteria: { fields: criteriaValues },
                })
            );
        });
    }

    abstract get instanceId();

    get searchName$(): Observable<string> {
        return this.search$.pipe(
            map((definition: SearchListEntry) => definition.name),
            distinctUntilChanged()
        );
    }

    get searchDisplayName$(): Observable<string> {
        return this.search$.pipe(
            map((definition: SearchListEntry) => definition.displayName),
            distinctUntilChanged()
        );
    }
}
